import { ref } from 'vue';
import { sourcesAPI, messengerAPI, googleCalendarAPI, servicesAPI } from '../services/api';

// Global state for header - persists across route changes
export const headerState = {
  isCalendarConnected: ref(false),
  isCalendarLoading: ref(false),
  isMessengerConnected: ref(false),
  isMessengerLoading: ref(false),
  messengerSourceId: ref<string | null>(null),
  hasServices: ref(false),
  isServicesLoading: ref(false),
  initialized: ref(false),
};

// Holds the in-flight init promise so concurrent guard calls all await the same load
let initPromise: Promise<void> | null = null;

export async function initHeaderState(_userId?: string, organizationId?: string) {
  if (headerState.initialized.value) {
    return;
  }

  if (!initPromise) {
    initPromise = Promise.all([
      loadCalendarStatus(),
      organizationId ? loadMessengerStatus(organizationId) : Promise.resolve(),
      loadServicesStatus(),
    ]).then(() => {
      headerState.initialized.value = true;
    });
  }

  await initPromise;
}

async function loadCalendarStatus() {
  headerState.isCalendarLoading.value = true;
  try {
    const response = await googleCalendarAPI.getStatus();
    headerState.isCalendarConnected.value = response.connected;
  } catch (error: any) {
    console.error('Error fetching Google Calendar status:', error);
    headerState.isCalendarConnected.value = false;
  } finally {
    headerState.isCalendarLoading.value = false;
  }
}

async function loadMessengerStatus(organizationId: string) {
  headerState.isMessengerLoading.value = true;
  try {
    const sources = await sourcesAPI.getAll();
    const messengerSource = sources.find((s) => s.name === 'Messenger');
    headerState.messengerSourceId.value = messengerSource?.id ?? null;

    if (headerState.messengerSourceId.value) {
      const pages = await messengerAPI.getPages(organizationId);
      headerState.isMessengerConnected.value = pages.length > 0;
    }
  } catch (error) {
    console.error('Error fetching Messenger status:', error);
  } finally {
    headerState.isMessengerLoading.value = false;
  }
}

async function loadServicesStatus() {
  headerState.isServicesLoading.value = true;
  try {
    const response = await servicesAPI.getAll({ page: 1, per_page: 1 });
    headerState.hasServices.value = response.total > 0;
  } catch (error: any) {
    console.error('Error fetching services status:', error);
    headerState.hasServices.value = false;
  } finally {
    headerState.isServicesLoading.value = false;
  }
}

export async function refreshCalendarStatus() {
  await loadCalendarStatus();
}

export async function refreshMessengerStatus(organizationId: string) {
  await loadMessengerStatus(organizationId);
}

export async function refreshServicesStatus() {
  await loadServicesStatus();
}

export function resetHeaderState() {
  headerState.isCalendarConnected.value = false;
  headerState.isCalendarLoading.value = false;
  headerState.isMessengerConnected.value = false;
  headerState.isMessengerLoading.value = false;
  headerState.messengerSourceId.value = null;
  headerState.hasServices.value = false;
  headerState.isServicesLoading.value = false;
  headerState.initialized.value = false;
  initPromise = null;
}
